import { useEffect, useState, useRef } from "react";
import { useSearchParams, Link, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import Header from "@/components/Header";
import { useAuth } from "@/lib/auth";
import { CheckCircle2, XCircle, Loader2 } from "lucide-react";

const MAX_POLLS = 8;
const POLL_INTERVAL_MS = 2000;

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const { user } = useAuth();
  const nav = useNavigate();
  const [state, setState] = useState("polling"); // polling | paid | expired | error | timeout
  const [txn, setTxn] = useState(null);
  const attemptsRef = useRef(0);

  useEffect(() => {
    if (!sessionId) { setState("error"); return; }
    let cancelled = false;

    const poll = async () => {
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (cancelled) return;
        setTxn(data);
        if (data.payment_status === "paid") {
          setState("paid");
          return;
        }
        if (data.status === "expired") {
          setState("expired");
          return;
        }
        attemptsRef.current += 1;
        if (attemptsRef.current >= MAX_POLLS) {
          setState("timeout");
          return;
        }
        setTimeout(poll, POLL_INTERVAL_MS);
      } catch (e) {
        if (cancelled) return;
        setState("error");
      }
    };

    poll();
    return () => { cancelled = true; };
  }, [sessionId]);

  const homeHref = user?.role === "coach" ? "/coach" : user?.role === "athletic_director" ? "/ad" : "/";

  return (
    <div className="min-h-screen bg-appbg text-white">
      <Header />
      <div className="px-4 md:px-8 lg:px-12 py-12 max-w-2xl" data-testid="payment-success-page">
        {state === "polling" && (
          <div className="text-center py-12">
            <Loader2 className="text-volt mx-auto animate-spin" size={48} />
            <div className="heading text-3xl mt-6">CONFIRMING PAYMENT…</div>
            <div className="text-neutral-400 text-sm mt-2">Hang tight. Don't close this tab.</div>
          </div>
        )}
        {state === "paid" && (
          <div className="border border-volt p-8 bg-[#0A101A]" data-testid="payment-paid-state">
            <CheckCircle2 className="text-volt" size={48} />
            <div className="heading text-4xl mt-4">PAYMENT CONFIRMED.</div>
            <div className="text-neutral-300 mt-3">
              <div>{txn?.package_id ? humanPackage(txn.package_id) : "Your purchase"} is active.</div>
              {txn?.amount ? <div className="text-mono text-volt mt-2 text-lg">${Number(txn.amount).toFixed(2)} USD</div> : null}
            </div>
            <div className="mt-6 flex gap-3">
              <Link to={homeHref} className="bg-blaze text-white heading text-base px-5 py-3 hover:glow-blaze transition-all" data-testid="back-home-btn">BACK TO DASHBOARD</Link>
              {txn?.category === "position_search" && txn?.position_search_id && (
                <Link to="/ad" className="border border-neutral-700 text-neutral-200 heading text-base px-5 py-3 hover:border-white" data-testid="continue-position-search-btn">GO TO DASHBOARD</Link>
              )}
            </div>
          </div>
        )}
        {state === "expired" && (
          <div className="border border-blaze p-8 bg-[#1A0A0A]" data-testid="payment-expired-state">
            <XCircle className="text-blaze" size={48} />
            <div className="heading text-4xl mt-4">SESSION EXPIRED.</div>
            <p className="text-neutral-300 mt-3">Your Stripe session expired before payment completed. Restart checkout to try again.</p>
            <button onClick={() => nav(-1)} className="mt-6 bg-blaze text-white heading text-base px-5 py-3 hover:glow-blaze" data-testid="payment-retry-btn">TRY AGAIN</button>
          </div>
        )}
        {state === "timeout" && (
          <div className="border border-neutral-700 p-8 bg-appcard" data-testid="payment-timeout-state">
            <Loader2 className="text-neutral-500" size={48} />
            <div className="heading text-4xl mt-4">STILL PROCESSING.</div>
            <p className="text-neutral-300 mt-3">Stripe is taking longer than usual. We've recorded your transaction — you can refresh this page in a minute, or check your dashboard.</p>
            <Link to={homeHref} className="inline-block mt-6 bg-blaze text-white heading text-base px-5 py-3 hover:glow-blaze" data-testid="timeout-home-btn">GO TO DASHBOARD</Link>
          </div>
        )}
        {state === "error" && (
          <div className="border border-blaze p-8 bg-[#1A0A0A]" data-testid="payment-error-state">
            <XCircle className="text-blaze" size={48} />
            <div className="heading text-4xl mt-4">COULDN'T VERIFY PAYMENT.</div>
            <p className="text-neutral-300 mt-3">No session id found, or the verification call failed. If you completed payment, your dashboard will reflect the upgrade within a minute.</p>
            <Link to={homeHref} className="inline-block mt-6 border border-neutral-700 text-neutral-200 heading text-base px-5 py-3 hover:border-white" data-testid="error-home-btn">DASHBOARD</Link>
          </div>
        )}
      </div>
    </div>
  );
}

function humanPackage(id) {
  return {
    program_recruiting_monthly: "Program Recruiting (Monthly)",
    coach_pro_monthly: "Coach Pro (Monthly)",
    coach_pro_yearly: "Coach Pro (Yearly)",
    program_monitor_monthly: "Always-On Monitor (Monthly)",
    program_monitor_yearly: "Always-On Monitor (Yearly)",
    position_supply: "Candidate Supply",
    position_screening: "Pre-Interview Screening",
    position_consult: "Hands-on Consultation",
  }[id] || id;
}
